/**
 * Die Erzeugungsanlagen einer Anlage (HM-122, ADR 0038): PV-Anlagen und
 * Speicher, **gegliedert nach dem Zähler, hinter dem sie stehen** (#42).
 *
 * **Das Modell** (#40, ADR 0038 Nachtrag): Der Zähler ist die Wahrheit über
 * das, was aus den Anlagen heraus- und in sie hineingeht (netto); ihre Geräte
 * liefern die Bruttowerte. Die Seite zeigt deshalb je Erzeugungszähler einen
 * Block — oben der Zähler, darunter seine Anlagen mit Gerät und Momentanwert —
 * und zuletzt die Anlagen hinter keinem Zähler. Welche Zähler das sind, sagt
 * `generationMeterLayout`.
 *
 * **Zugeordnet wird hier und im Messkonzept** (#42, löst HM-124 ab). Beide
 * schreiben dasselbe Feld der Anlage (`meteringPointId`); zwei Orte für
 * dieselbe Angabe sind keine zwei Wahrheiten. Im Dialog einer Anlage heißt es
 * „Zähler".
 *
 * **Und eine Anlage hat Geräte** (HM-129, HM-130): Wechselrichter, Laderegler
 * am Victron GX, BMS — eines oder mehrere, und ein Hybrid steht an zwei
 * Anlagen. Die Zeile zeigt, was sie zusammen melden, nur für den Teil, der zur
 * Art gehört.
 *
 * **Der Zähler darf fehlen.** Ein Balkonkraftwerk ohne Messsteckdose ist
 * trotzdem eine Anlage — nur eine ohne Erzeugung im Messkonzept, und die Seite
 * sagt das („nicht gemessen"), statt eine Zahl zu raten (Regel 9).
 *
 * **Und wo kein echter Erzeugungszähler ist, entsteht hier der virtuelle**
 * (HM-123, ADR 0038 §2). Ohne irgendeine Meldung gibt es ihn nicht; eine
 * Erzeugung ohne Messung wird nicht erfunden (OQ-057).
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  breakpoints,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  Input,
  Select,
  icons,
  useNarrow,
} from "@hausmeter/ui";
import { t } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { SectionHeading } from "../components/section.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import {
  useConceptTemplate,
  useDevices,
  useGenerationAssets,
  useGenerationAssetState,
  useMeters,
  useSiteConcepts,
} from "../lib/queries.js";
import { deviceTypeInfo } from "../lib/device-type.js";
import { assetStateGap, assetStateLine, fitsAssetKind } from "../lib/generation-asset-device.js";
import { useSiteAccess } from "../lib/site-access.js";
import { meterNumberText } from "../lib/meter-labels.js";
import {
  generationMeterLayout,
  generationMeterOptions,
  type GenerationMeterGroup,
  type GenerationMeterLayout,
} from "../lib/generation-meters.js";

type GenerationAsset = ApiComponents["schemas"]["GenerationAsset"];
type Kind = GenerationAsset["kind"];
type CreateRequest = ApiComponents["schemas"]["CreateGenerationAssetRequest"];
type UpdateRequest = ApiComponents["schemas"]["UpdateGenerationAssetRequest"];
type Meter = ApiComponents["schemas"]["Meter"];
type Device = ApiComponents["schemas"]["Device"];
type CreateMeterRequest = ApiComponents["schemas"]["CreateMeterRequest"];

const CLIP = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;
const MUTED = { color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 } as const;
const KINDS: readonly Kind[] = ["pv", "battery"];

const format = (value: number, digits = 2): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(value);

/**
 * Die Kopfleiste einer geöffneten Erzeugungsanlage (HM-131) — Name, darunter
 * Art, Kennzahl, Ausrichtung und die Zahl der Geräte. Wie `meterHeadline`: Der
 * Rahmen ruft sie und weiß nicht, was eine Ausrichtung ist.
 */
export function generationAssetHeadline(asset: GenerationAsset): {
  title: string;
  subtitle: string;
} {
  const parts = [
    kindLabel(asset.kind),
    ratingText(asset),
    orientationText(asset),
    asset.deviceIds.length === 0
      ? "kein Gerät"
      : asset.deviceIds.length === 1
        ? "1 Gerät"
        : `${asset.deviceIds.length} Geräte`,
  ].filter((part) => part.length > 0);
  return { title: asset.label, subtitle: parts.join(" · ") };
}

export function kindLabel(kind: Kind): string {
  return t(`generationAsset.kind.${kind}`, "de-DE");
}

/** Die eine Kennzahl je Art: Nennleistung oder Kapazität. */
export function ratingText(asset: GenerationAsset): string {
  if (asset.kind === "pv")
    return asset.peakPowerKwp === null ? "" : `${format(asset.peakPowerKwp)} kWp`;
  return asset.capacityKwh === null ? "" : `${format(asset.capacityKwh)} kWh`;
}

/** Ausrichtung und Neigung in einer Zeile — nur, was eingetragen ist. */
export function orientationText(asset: GenerationAsset): string {
  const parts: string[] = [];
  if (asset.azimuthDeg !== null)
    parts.push(`${format(asset.azimuthDeg, 0)}° ${compass(asset.azimuthDeg)}`);
  if (asset.tiltDeg !== null) parts.push(`${format(asset.tiltDeg, 0)}° Neigung`);
  return parts.join(" · ");
}

/** Die Himmelsrichtung zu einem Azimut — für Menschen; gerechnet wird mit der Zahl. */
function compass(azimuth: number): string {
  const names = ["N", "NO", "O", "SO", "S", "SW", "W", "NW"];
  return names[Math.round((((azimuth % 360) + 360) % 360) / 45) % 8] ?? "";
}

/**
 * Die Gliederung der Seite und die Auswahl im Dialog aus denselben Daten — das
 * gültige Messkonzept, die Zähler, die Anlagen. Solange eines davon lädt, ist
 * `layout` `null`; ohne gültiges Messkonzept zählen nur virtuelle und
 * Erzeugungszähler. **Scheitert eine der Abfragen, wird nicht gegliedert**:
 * Ohne Vorlage fehlten die Zähler der Erzeugungs-Steckplätze still, und die
 * Seite behauptete, es gebe keinen.
 */
function useGenerationMeterLayout(siteId: string | null): {
  readonly layout: GenerationMeterLayout | null;
  readonly failed: boolean;
} {
  const assets = useGenerationAssets(siteId);
  const meters = useMeters(siteId, true);
  const concepts = useSiteConcepts(siteId);
  const active = concepts.data?.concepts.find((concept) => concept.status === "active") ?? null;
  const expanded = useConceptTemplate(
    active === null || active.templateKey === null ? null : active.id,
  );
  if (assets.isError || meters.isError || concepts.isError || expanded.isError)
    return { layout: null, failed: true };
  if (assets.data === undefined || meters.data === undefined || concepts.data === undefined)
    return { layout: null, failed: false };
  if (active !== null && active.templateKey !== null && expanded.data === undefined)
    return { layout: null, failed: false };
  return {
    layout: generationMeterLayout({
      concept: active,
      template: expanded.data ?? null,
      meters: meters.data.items,
      assets: assets.data.items,
    }),
    failed: false,
  };
}

export function GenerationAssetsScreen({ siteId }: { siteId: string | null }) {
  const [editing, setEditing] = useState<GenerationAsset | "new" | null>(null);
  const [composing, setComposing] = useState(false);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();
  const devices = useDevices(siteId);
  const { layout, failed } = useGenerationMeterLayout(siteId);
  const deviceList = devices.data?.items ?? [];
  const empty = layout !== null && layout.groups.length === 0 && layout.unmetered.length === 0;
  const onEdit = rights.canWrite ? setEditing : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100 }}>
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Erzeugungsanlagen"
          subtitle="PV-Anlagen und Speicher — je Zähler, hinter dem sie stehen. Der Zähler misst netto, die Geräte der Anlagen zeigen, was dahinter passiert."
          action={
            rights.canWrite ? (
              <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={siteId === null}
                  onClick={() => setComposing(true)}
                >
                  <icons.IconGauge size={15} />
                  Anlagen zusammenschalten
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={siteId === null}
                  onClick={() => setEditing("new")}
                >
                  <icons.IconPlus size={15} />
                  Erzeugungsanlage anlegen
                </Button>
              </span>
            ) : undefined
          }
        />

        {siteId !== null && failed && (
          <Alert tone="danger">
            Die Erzeugungsanlagen und ihre Zähler konnten nicht geladen werden.
          </Alert>
        )}
        {siteId !== null && layout === null && !failed && (
          <Card>
            <div>Wird geladen …</div>
          </Card>
        )}

        {(siteId === null || empty) && (
          <Card padding="none">
            <EmptyState title="Noch keine Erzeugungsanlage" icon={<icons.IconSun size={30} />}>
              Eine Erzeugungsanlage ist eine PV-Anlage oder ein Speicher. Sie steht hinter einem
              Zähler: <strong>Der Zähler misst, was aus ihr heraus- und in sie hineingeht</strong>,
              ihre Geräte zeigen, was dahinter passiert. Mit Nennleistung, Ausrichtung und Neigung
              zeigt „Strom &amp; Erzeugung" den Ertrag je kWp.
            </EmptyState>
          </Card>
        )}

        {siteId !== null &&
          layout !== null &&
          layout.groups.map((group) => (
            <MeterGroup
              key={group.meteringPointId}
              siteId={siteId}
              group={group}
              devices={deviceList}
              onEdit={onEdit}
              canDelete={rights.canDelete}
            />
          ))}

        {siteId !== null && layout !== null && layout.unmetered.length > 0 && (
          <Card padding="none">
            <GroupHeader
              icon={<icons.IconSun size={17} />}
              title="Ohne Zähler"
              subtitle={
                <>
                  <Badge tone="neutral">nicht gemessen</Badge> Diese Anlagen gehen in keine Bilanz
                  ein. {rights.canWrite ? "Unter „Ändern“ wählst du ihren Zähler." : ""}
                </>
              }
            />
            <AssetRows
              siteId={siteId}
              assets={layout.unmetered}
              devices={deviceList}
              onEdit={onEdit}
              canDelete={rights.canDelete}
            />
          </Card>
        )}

        {layout !== null && !empty && (
          <p style={MUTED}>
            Welches <strong>Gerät</strong> eine Anlage ist — Wechselrichter, Laderegler, BMS — und
            hinter welchem <strong>Zähler</strong> sie steht, steht unter „Ändern“. Im Messkonzept
            lässt sich die Zuordnung ebenso ändern, am Kasten „Erzeugungsanlagen" unter dem
            Erzeugungszähler. In die Bilanz geht eine Anlage über ihren Zähler ein.
          </p>
        )}
      </section>

      {rights.canWrite && composing && siteId !== null && (
        <VirtualMeterDialog siteId={siteId} onClose={() => setComposing(false)} />
      )}
      {rights.canWrite && editing !== null && siteId !== null && (
        <GenerationAssetDialog
          siteId={siteId}
          asset={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Ein Link wie in jeder Liste — Mittelklick und Strg öffnen ein neues Fenster. */
function RouteLink({
  route,
  children,
  style,
}: {
  route: Route;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <a
      href={routePath(route)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(route);
      }}
      style={{ color: "var(--text-strong)", textDecoration: "none", fontWeight: 600, ...style }}
    >
      {children}
    </a>
  );
}

function GroupHeader({
  icon,
  title,
  subtitle,
  aside,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        padding: "14px var(--sp-5)",
        borderBottom: "1px solid var(--border-subtle)",
        background: "var(--surface-inset)",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, color: "var(--text-muted)" }}>
        {icon}
      </span>
      <span style={{ minWidth: 0, flex: "1 1 240px" }}>
        <span style={{ display: "block", fontSize: 14.5, fontWeight: 700 }}>{title}</span>
        <span
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--text-muted)",
            marginTop: 2,
          }}
        >
          {subtitle}
        </span>
      </span>
      {aside}
    </div>
  );
}

/**
 * Ein Erzeugungszähler und was hinter ihm steht. Der Kopf nennt den Zähler, seine
 * Art und seinen Steckplatz im Messkonzept; seine Werte stehen auf seiner Seite.
 * **Netto rechnet die Oberfläche hier nicht**: Welche Richtung eines Zählwerks
 * Abgabe ist, sagt erst die Rolle im Messkonzept (`live-now.ts`), und ein
 * Zähler außerhalb des Konzepts hat keine.
 */
function MeterGroup({
  siteId,
  group,
  devices,
  onEdit,
  canDelete,
}: {
  siteId: string;
  group: GenerationMeterGroup;
  devices: readonly Device[];
  onEdit: ((asset: GenerationAsset) => void) | undefined;
  canDelete: boolean;
}) {
  const { meter } = group;
  const meterRoute: Route | null =
    meter === null
      ? null
      : { kind: "meter", siteId, meterId: meter.id, tab: "overview", register: null, range: "24h" };
  const count = group.assets.length;
  return (
    <Card padding="none">
      <GroupHeader
        icon={<icons.IconGauge size={17} />}
        title={
          meterRoute === null || meter === null ? (
            "Kein Zähler mehr an dieser Messstelle"
          ) : (
            <RouteLink route={meterRoute}>{meter.label}</RouteLink>
          )
        }
        subtitle={
          <>
            {meter?.virtual === true && <Badge tone="info">virtuell</Badge>}
            {meter?.builtIntoDevice === true && <Badge tone="neutral">Messwerk im Gerät</Badge>}
            {group.slotLabel === null ? (
              <span>in keinem Steckplatz des Messkonzepts</span>
            ) : (
              <span>im Messkonzept: {group.slotLabel}</span>
            )}
          </>
        }
        aside={
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            {count === 0 ? "keine Anlage" : count === 1 ? "1 Anlage" : `${count} Anlagen`}
          </span>
        }
      />
      {count === 0 ? (
        <p style={{ ...MUTED, padding: "12px var(--sp-5)" }}>
          Hinter diesem Zähler steht noch keine Anlage. Er misst dann eine Erzeugung, von der
          niemand weiß, woher sie kommt.
        </p>
      ) : (
        <AssetRows
          siteId={siteId}
          assets={group.assets}
          devices={devices}
          onEdit={onEdit}
          canDelete={canDelete}
        />
      )}
    </Card>
  );
}

function AssetRows({
  siteId,
  assets,
  devices,
  onEdit,
  canDelete,
}: {
  siteId: string;
  assets: readonly GenerationAsset[];
  devices: readonly Device[];
  /** Fehlt, wenn die Teilung kein Ändern erlaubt (HM-160). */
  onEdit: ((asset: GenerationAsset) => void) | undefined;
  canDelete: boolean;
}) {
  const narrow = useNarrow(breakpoints.table);
  const columns = narrow ? "minmax(0, 1fr) auto" : "1.5fr 0.8fr 1.6fr auto";

  return (
    <div role="table" aria-label="Erzeugungsanlagen">
      {!narrow && (
        <div
          role="row"
          style={{
            display: "grid",
            gridTemplateColumns: columns,
            gap: "var(--sp-3)",
            alignItems: "center",
            padding: "9px var(--sp-5)",
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "var(--tracking-wide)",
            textTransform: "uppercase",
            color: "var(--text-faint)",
          }}
        >
          <span role="columnheader">Erzeugungsanlage</span>
          <span role="columnheader">Kennzahl</span>
          <span role="columnheader">Gerät · jetzt</span>
          <span role="columnheader" />
        </div>
      )}

      {assets.map((asset, index) => (
        <div
          key={asset.id}
          role="row"
          style={{
            display: "grid",
            gridTemplateColumns: columns,
            gap: "var(--sp-3)",
            alignItems: "center",
            padding: "13px var(--sp-5)",
            borderBottom: index < assets.length - 1 ? "1px solid var(--border-subtle)" : "none",
          }}
        >
          <div role="cell" style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "var(--radius-sm)",
                background: "var(--surface-inset)",
                color: "var(--text-muted)",
              }}
            >
              {asset.kind === "pv" ? <icons.IconSun size={17} /> : <icons.IconBolt size={17} />}
            </span>
            <span style={{ minWidth: 0 }}>
              {/* Der Name ist der Weg zur Detailseite (HM-131). */}
              <RouteLink
                route={{ kind: "generation-asset", siteId, assetId: asset.id, range: "24h" }}
                style={{ display: "block", fontSize: 14, ...CLIP }}
              >
                {asset.label}
              </RouteLink>
              <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", ...CLIP }}>
                {[kindLabel(asset.kind), narrow ? ratingText(asset) : null, orientationText(asset)]
                  .filter((part) => part !== null && part.length > 0)
                  .join(" · ")}
              </span>
            </span>
          </div>
          {!narrow && (
            <span
              role="cell"
              style={{
                fontSize: 13,
                color: "var(--text-body)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {ratingText(asset)}
            </span>
          )}
          {!narrow && (
            <span role="cell" style={{ minWidth: 0 }}>
              <AssetDevices
                siteId={siteId}
                asset={asset}
                devices={devices.filter((device) => asset.deviceIds.includes(device.id))}
              />
            </span>
          )}
          <span role="cell" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
            {onEdit !== undefined && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(asset)}>
                Ändern
              </Button>
            )}
            {canDelete && <DeleteButton asset={asset} />}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Löschen in zwei Schritten — an der Anlage hängen keine Werte, aber ein Klick ist keine Absicht. */
/**
 * Die Geräte der Anlage und was sie zusammen melden (HM-129, HM-130). Ohne
 * Gerät ein graues „kein Gerät" — wie „nicht gemessen" eine Aussage und kein
 * Schaden. Ein Gerät, das nicht mehr in der Liste dieser Anlage steht
 * (freigegeben, umgezogen), fehlt hier; die API liest es ebenfalls wie keines.
 */
function AssetDevices({
  siteId,
  asset,
  devices,
}: {
  siteId: string;
  asset: GenerationAsset;
  devices: readonly Device[];
}) {
  const state = useGenerationAssetState(devices.length === 0 ? null : asset.id);
  if (devices.length === 0) return <Badge tone="neutral">kein Gerät</Badge>;

  const line = state.data === undefined ? null : assetStateLine(state.data);
  const gap = state.data === undefined ? null : assetStateGap(state.data);
  return (
    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ fontSize: 13, ...CLIP }}>
        {devices.map((device, index) => {
          const route: Route = { kind: "device", siteId, deviceId: device.id, tab: "overview" };
          return (
            <span key={device.id}>
              {index > 0 && ", "}
              <a
                href={routePath(route)}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                    return;
                  event.preventDefault();
                  navigate(route);
                }}
                style={{ color: "var(--text-strong)", textDecoration: "none", fontWeight: 600 }}
              >
                {device.name}
              </a>
            </span>
          );
        })}
      </span>
      <span
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          fontVariantNumeric: "tabular-nums",
          ...CLIP,
        }}
      >
        {state.isError ? "Zustand nicht abrufbar" : (line ?? gap ?? "")}
      </span>
    </span>
  );
}

/**
 * Löschen mit Rückfrage — in der Zeile der Liste und im roten Kasten der
 * Detailseite (HM-131); dort führt `onDeleted` zurück zur Liste.
 */
export function DeleteButton({
  asset,
  onDeleted,
  label = "Löschen",
}: {
  asset: GenerationAsset;
  onDeleted?: () => void;
  label?: string;
}) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE("/generation-assets/{generationAssetId}", {
        params: { path: { generationAssetId: asset.id } },
      });
      if (error) throw new Error(apiErrorMessage(error) ?? "Löschen fehlgeschlagen.");
    },
    onSuccess: async () => {
      onDeleted?.();
      await client.invalidateQueries({ queryKey: ["generation-assets"] });
      await client.invalidateQueries({ queryKey: ["site-energy"] });
    },
  });
  if (!confirming) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
        {label}
      </Button>
    );
  }
  return (
    <>
      <Button
        variant="danger"
        size="sm"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
      >
        {remove.isPending ? "Wird gelöscht …" : "Wirklich löschen"}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
        Abbrechen
      </Button>
    </>
  );
}

/**
 * `null` für leer, `undefined` für Unsinn, sonst die Zahl — dieselbe Prüfung
 * wie bei der Wohnfläche: Bei Unsinn wird nicht abgeschickt (Regel 9). Ein
 * Komma gilt als Dezimaltrenner.
 */
function parseNumber(
  raw: string,
  min = 0,
  max = Number.POSITIVE_INFINITY,
  allowZero = false,
): number | null | undefined {
  const text = raw.trim();
  if (text.length === 0) return null;
  const value = Number(text.replace(",", "."));
  if (!Number.isFinite(value)) return undefined;
  if (value < min || value > max || (!allowZero && value <= 0)) return undefined;
  return value;
}

const numberText = (value: number | null): string =>
  value === null ? "" : String(value).replace(".", ",");

/**
 * Anlegen und Ändern in **einem** Dialog — zwei Formulare liefen beim nächsten
 * Feld auseinander. Die Felder folgen der Art: Eine PV-Anlage fragt nach
 * Nennleistung, Ausrichtung und Neigung, ein Speicher nach Kapazität und
 * Leistungsgrenzen; was zur anderen Art gehört, geht beim Wechsel mit `null` mit.
 *
 * **Und nach dem Zähler, hinter dem die Anlage steht** (#42). Zur Wahl stehen die
 * Erzeugungszähler aus `generationMeterLayout` — dieselben, die die Seite als
 * Blöcke zeigt. Beim Ändern geht `meteringPointId` nur mit, wenn er sich geändert
 * hat: Weglassen heißt behalten, und eine Zuordnung, die gleichzeitig im
 * Messkonzept geändert wurde, überschreibt ein unberührtes Feld nicht.
 */
export function GenerationAssetDialog({
  siteId,
  asset,
  meteringPointId = null,
  initialKind = "pv",
  onClose,
}: {
  siteId: string;
  asset: GenerationAsset | null;
  /** Die Art einer **neuen** Anlage — vorgewählt aus dem, was am Erzeugungszähler schon steht. */
  initialKind?: Kind;
  /**
   * Der vorgewählte Zähler einer **neuen** Anlage (HM-124) — gesetzt, wenn der
   * Dialog aus dem Messkonzept am Erzeugungszähler geöffnet wird.
   */
  meteringPointId?: string | null;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [kind, setKind] = useState<Kind>(asset?.kind ?? initialKind);
  const [label, setLabel] = useState(asset?.label ?? "");
  const [commissionedOn, setCommissionedOn] = useState(asset?.commissionedOn ?? "");
  const [peak, setPeak] = useState(numberText(asset?.peakPowerKwp ?? null));
  const [azimuth, setAzimuth] = useState(numberText(asset?.azimuthDeg ?? null));
  const [tilt, setTilt] = useState(numberText(asset?.tiltDeg ?? null));
  const [inverter, setInverter] = useState(numberText(asset?.inverterPowerKw ?? null));
  const [capacity, setCapacity] = useState(numberText(asset?.capacityKwh ?? null));
  const [usable, setUsable] = useState(numberText(asset?.usableCapacityKwh ?? null));
  const [charge, setCharge] = useState(numberText(asset?.maxChargePowerKw ?? null));
  const [discharge, setDischarge] = useState(numberText(asset?.maxDischargePowerKw ?? null));
  const [deviceIds, setDeviceIds] = useState<readonly string[]>(asset?.deviceIds ?? []);
  const [pointId, setPointId] = useState<string>(
    (asset === null ? meteringPointId : asset.meteringPointId) ?? "",
  );
  const meterGroups = useGenerationMeterLayout(siteId).layout?.groups ?? null;
  const chosenPoint = pointId.length > 0 ? pointId : null;
  const [error, setError] = useState<string | null>(null);
  /**
   * Angeboten werden die Geräte dieser Anlage, die zur Art passen (HM-129,
   * HM-130) — Wechselrichter und Laderegler für PV, Speicher und Wechselrichter
   * für eine Batterie, beliebig viele. Ein Gerät, das nach einem Wechsel der
   * Art nicht mehr passt, fällt aus der Wahl, statt still mitgespeichert zu
   * werden.
   */
  const deviceOptions = (useDevices(siteId).data?.items ?? []).filter((device) =>
    fitsAssetKind(kind, device.deviceType),
  );
  const chosenDevices = deviceIds.filter((id) => deviceOptions.some((device) => device.id === id));
  const toggleDevice = (id: string, on: boolean) =>
    setDeviceIds((current) => (on ? [...current, id] : current.filter((entry) => entry !== id)));

  const values = {
    peakPowerKwp: parseNumber(peak),
    azimuthDeg: parseNumber(azimuth, 0, 360, true),
    tiltDeg: parseNumber(tilt, 0, 90, true),
    inverterPowerKw: parseNumber(inverter),
    capacityKwh: parseNumber(capacity),
    usableCapacityKwh: parseNumber(usable),
    maxChargePowerKw: parseNumber(charge),
    maxDischargePowerKw: parseNumber(discharge),
  };
  const pvValid =
    kind !== "pv" ||
    (values.peakPowerKwp !== null &&
      values.peakPowerKwp !== undefined &&
      values.azimuthDeg !== undefined &&
      values.tiltDeg !== undefined &&
      values.inverterPowerKw !== undefined);
  const batteryValid =
    kind !== "battery" ||
    (values.capacityKwh !== null &&
      values.capacityKwh !== undefined &&
      values.usableCapacityKwh !== undefined &&
      values.maxChargePowerKw !== undefined &&
      values.maxDischargePowerKw !== undefined &&
      (values.usableCapacityKwh === null || values.usableCapacityKwh <= values.capacityKwh));
  const valid = label.trim().length > 0 && pvValid && batteryValid;

  const save = useMutation({
    mutationFn: async (): Promise<GenerationAsset> => {
      const pv = kind === "pv";
      const common = { kind, label: label.trim() };
      if (asset === null) {
        const body: CreateRequest = {
          ...common,
          meteringPointId: chosenPoint,
          deviceIds: chosenDevices,
          ...(commissionedOn.length > 0 ? { commissionedOn } : {}),
          ...(pv && values.peakPowerKwp ? { peakPowerKwp: values.peakPowerKwp } : {}),
          ...(pv && values.azimuthDeg != null ? { azimuthDeg: values.azimuthDeg } : {}),
          ...(pv && values.tiltDeg != null ? { tiltDeg: values.tiltDeg } : {}),
          ...(pv && values.inverterPowerKw ? { inverterPowerKw: values.inverterPowerKw } : {}),
          ...(!pv && values.capacityKwh ? { capacityKwh: values.capacityKwh } : {}),
          ...(!pv && values.usableCapacityKwh
            ? { usableCapacityKwh: values.usableCapacityKwh }
            : {}),
          ...(!pv && values.maxChargePowerKw ? { maxChargePowerKw: values.maxChargePowerKw } : {}),
          ...(!pv && values.maxDischargePowerKw
            ? { maxDischargePowerKw: values.maxDischargePowerKw }
            : {}),
        };
        const { data, error: failure } = await api.POST("/sites/{siteId}/generation-assets", {
          params: { path: { siteId } },
          body,
        });
        if (failure || !data)
          throw new Error(
            apiErrorMessage(failure) ?? "Die Erzeugungsanlage ließ sich nicht anlegen.",
          );
        return data;
      }
      /* Beim Ändern heißt `null` löschen: Die Felder der anderen Art gehen mit
         `null` mit. `meteringPointId` nur, wenn er sich geändert hat (#42) —
         was fehlt, bleibt stehen. */
      const body: UpdateRequest = {
        ...common,
        ...(chosenPoint !== asset.meteringPointId ? { meteringPointId: chosenPoint } : {}),
        deviceIds: chosenDevices,
        commissionedOn: commissionedOn.length > 0 ? commissionedOn : null,
        peakPowerKwp: pv ? (values.peakPowerKwp ?? null) : null,
        azimuthDeg: pv ? (values.azimuthDeg ?? null) : null,
        tiltDeg: pv ? (values.tiltDeg ?? null) : null,
        inverterPowerKw: pv ? (values.inverterPowerKw ?? null) : null,
        capacityKwh: pv ? null : (values.capacityKwh ?? null),
        usableCapacityKwh: pv ? null : (values.usableCapacityKwh ?? null),
        maxChargePowerKw: pv ? null : (values.maxChargePowerKw ?? null),
        maxDischargePowerKw: pv ? null : (values.maxDischargePowerKw ?? null),
      };
      const { data, error: failure } = await api.PATCH("/generation-assets/{generationAssetId}", {
        params: { path: { generationAssetId: asset.id } },
        body,
      });
      if (failure || !data)
        throw new Error(apiErrorMessage(failure) ?? "Die Änderung ließ sich nicht speichern.");
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["generation-assets"] });
      await client.invalidateQueries({ queryKey: ["site-energy"] });
      onClose();
      /* Der Zustand hängt am Gerät; `["generation-assets"]` trifft ihn mit — und
         auch die Anlage auf ihrer Detailseite und im Messkonzept. */
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const numberField = (
    labelText: string,
    value: string,
    set: (next: string) => void,
    hint: string,
    invalid: boolean,
    required = false,
  ) => (
    <Input
      label={labelText}
      value={value}
      inputMode="decimal"
      required={required}
      hint={hint}
      error={invalid ? "Bitte eine gültige Zahl." : undefined}
      onChange={(event) => set(event.target.value)}
    />
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={asset === null ? "Erzeugungsanlage anlegen" : "Erzeugungsanlage ändern"}
      description="Was hinter einem Zähler steht — und die Angaben, mit denen sich der Ertrag einordnen lässt."
      width={560}
      footer={
        <>
          <Button
            type="submit"
            form="generation-asset-form"
            size="sm"
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "Wird gespeichert …" : asset === null ? "Anlegen" : "Speichern"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
        </>
      }
    >
      <form
        id="generation-asset-form"
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: "var(--sp-3)",
          }}
        >
          <Select
            label="Art"
            value={kind}
            onChange={(event) => setKind(event.target.value as Kind)}
            options={KINDS.map((entry) => ({ value: entry, label: kindLabel(entry) }))}
          />
          <Input
            label="Bezeichnung"
            value={label}
            maxLength={120}
            required
            placeholder={kind === "pv" ? "Süddach" : "Batterie Keller"}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>
        <Select
          label="Zähler"
          value={pointId}
          disabled={meterGroups === null}
          onChange={(event) => setPointId(event.target.value)}
          options={[
            { value: "", label: "keiner – nicht gemessen" },
            ...generationMeterOptions(meterGroups ?? []),
            /* Was gewählt ist, steht immer zur Wahl — sonst zeigte das Feld „keiner"
               und speicherte doch die Messstelle (vorgewählt aus dem Messkonzept,
               solange die Liste lädt). */
            ...(pointId.length > 0 &&
            !(meterGroups ?? []).some((g) => g.meteringPointId === pointId)
              ? [
                  {
                    value: pointId,
                    label: meterGroups === null ? "wird geladen …" : "bisherige Messstelle",
                  },
                ]
              : []),
          ]}
        />
        <p style={MUTED}>
          Der Zähler misst, was aus der Anlage heraus- und in sie hineingeht. Ohne Zähler geht sie
          in keine Bilanz ein. Dieselbe Zuordnung steht im Messkonzept am Erzeugungszähler.
        </p>
        <Input
          label="Inbetriebnahme"
          type="date"
          value={commissionedOn}
          hint="Freiwillig — für den Vergleich nach Jahren."
          onChange={(event) => setCommissionedOn(event.target.value)}
        />
        <fieldset
          style={{
            border: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <legend
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--text-strong)",
              marginBottom: 4,
              padding: 0,
            }}
          >
            Geräte
          </legend>
          {deviceOptions.map((device) => (
            <Checkbox
              key={device.id}
              checked={chosenDevices.includes(device.id)}
              onChange={(on) => toggleDevice(device.id, on)}
              label={`${device.name} · ${deviceTypeInfo(device.deviceType).model}`}
            />
          ))}
        </fieldset>
        <p style={MUTED}>
          {deviceOptions.length === 0
            ? kind === "pv"
              ? "An dieser Anlage gibt es noch keinen Wechselrichter oder Laderegler. Zugelassene Geräte des Gateways erscheinen hier."
              : "An dieser Anlage gibt es noch keinen Speicher und keinen Wechselrichter. Zugelassene Geräte des Gateways erscheinen hier."
            : kind === "pv"
              ? "Die Wechselrichter oder Laderegler dieser Anlage — bei zwei Mikrowechselrichtern beide. Die Zeile zeigt danach ihre Leistung zusammen."
              : "Das BMS, der Speicher oder der Hybrid-Wechselrichter — bei zwei Batterien beide. Die Zeile zeigt danach Ladezustand und Richtung."}
        </p>

        {kind === "pv" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
              gap: "var(--sp-3)",
            }}
          >
            {numberField(
              "Nennleistung in kWp",
              peak,
              setPeak,
              "Die Leistung der Module vom Datenblatt.",
              values.peakPowerKwp === undefined,
              true,
            )}
            {numberField(
              "Wechselrichter in kW",
              inverter,
              setInverter,
              "Freiwillig. Beim Balkonkraftwerk 0,8.",
              values.inverterPowerKw === undefined,
            )}
            {numberField(
              "Ausrichtung in Grad",
              azimuth,
              setAzimuth,
              "0 = Nord, 90 = Ost, 180 = Süd, 270 = West. Freiwillig.",
              values.azimuthDeg === undefined,
            )}
            {numberField(
              "Neigung in Grad",
              tilt,
              setTilt,
              "0 = flach, 90 = senkrecht. Freiwillig.",
              values.tiltDeg === undefined,
            )}
          </div>
        )}
        {kind === "battery" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
              gap: "var(--sp-3)",
            }}
          >
            {numberField(
              "Nennkapazität in kWh",
              capacity,
              setCapacity,
              "Vom Datenblatt.",
              values.capacityKwh === undefined,
              true,
            )}
            {numberField(
              "Nutzbare Kapazität in kWh",
              usable,
              setUsable,
              "Freiwillig, nie mehr als die Nennkapazität.",
              values.usableCapacityKwh === undefined ||
                (values.usableCapacityKwh !== null &&
                  values.capacityKwh != null &&
                  values.usableCapacityKwh > values.capacityKwh),
            )}
            {numberField(
              "Ladeleistung in kW",
              charge,
              setCharge,
              "Freiwillig.",
              values.maxChargePowerKw === undefined,
            )}
            {numberField(
              "Entladeleistung in kW",
              discharge,
              setDischarge,
              "Freiwillig.",
              values.maxDischargePowerKw === undefined,
            )}
          </div>
        )}

        <p style={MUTED}>
          {kind === "pv"
            ? "Nennleistung, Ausrichtung und Neigung sind die Eingaben einer Ertragsprognose; heute rechnet daraus nur der Ertrag je kWp."
            : "Kapazität und Leistungsgrenzen ordnen ein, was die Geräte melden. In die Bilanz geht der Speicher über den Erzeugungszähler ein, hinter dem er steht."}
        </p>
        {error !== null && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}

/**
 * **Anlagen zusammenschalten** (HM-123, seit #42 aus Anlagen): der virtuelle
 * Zähler, wo kein echter misst.
 *
 * Gewählt werden **Erzeugungsanlagen und Erzeugungszähler**, nie ein Gerät oder
 * ein Datenpunkt — welche Stände mit welchem Vorzeichen eingehen, leitet die
 * API ab (`virtualSourcePlan`): Ertrag und Entladung hinzu, Ladung ab; mit einem
 * Speicher zählt der Zähler auch die Aufnahme. Die gewählten Anlagen stehen
 * danach hinter ihm. Eine Anlage ohne Gerät mit Energiestand lehnt die API ab
 * und sagt, woran es liegt; die Auswahl hier sperrt sie nicht vorher, weil nur
 * die API den Katalog des Geräts kennt.
 */
function VirtualMeterDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const client = useQueryClient();
  const assets = useGenerationAssets(siteId).data?.items ?? [];
  const layout = useGenerationMeterLayout(siteId).layout;
  const meters = (useMeters(siteId).data?.items ?? []).filter(
    (meter) =>
      meter.kind === "generation" &&
      !meter.virtual &&
      meter.commodity === "electricity" &&
      (meter.status === "active" || meter.status === "draft"),
  );
  const [label, setLabel] = useState("Erzeugung (virtuell)");
  const [assetIds, setAssetIds] = useState<readonly string[]>([]);
  const [meterIds, setMeterIds] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const valid = label.trim().length > 0 && assetIds.length + meterIds.length > 0;
  const toggle =
    (set: (update: (current: readonly string[]) => readonly string[]) => void, id: string) =>
    (on: boolean) =>
      set((current) => (on ? [...current, id] : current.filter((entry) => entry !== id)));
  const groupOf = (asset: GenerationAsset) =>
    layout?.groups.find((entry) => entry.meteringPointId === asset.meteringPointId) ?? null;

  const create = useMutation({
    mutationFn: async (): Promise<Meter> => {
      const body: CreateMeterRequest = {
        siteId,
        label: label.trim(),
        kind: "generation",
        commodity: "electricity",
        meteringPointLabel: label.trim(),
        virtual: true,
        generationAssetIds: [...assetIds],
        sourceMeterIds: [...meterIds],
        registers: [{ role: "energy_import_total" }],
      };
      const { data, error: failure } = await api.POST("/meters", { body });
      if (failure || !data)
        throw new Error(apiErrorMessage(failure) ?? "Die Anlagen ließen sich nicht zusammenschalten.");
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meters"] });
      await client.invalidateQueries({ queryKey: ["metering-points"] });
      await client.invalidateQueries({ queryKey: ["generation-assets"] });
      onClose();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const heading = (text: string) => (
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "var(--tracking-wide)",
        textTransform: "uppercase",
        color: "var(--text-faint)",
      }}
    >
      {text}
    </span>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Anlagen zusammenschalten"
      description="Für Anlagen ohne eigenen Erzeugungszähler: Ein virtueller Zähler zählt, was sie zusammen abgeben und — mit Speicher — aufnehmen."
      width={620}
      footer={
        <>
          <Button size="sm" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Wird angelegt …" : "Zusammenschalten"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <Input
          label="Bezeichnung"
          value={label}
          maxLength={120}
          required
          onChange={(event) => setLabel(event.target.value)}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {heading("Erzeugungsanlagen")}
          {assets.length === 0 && <p style={MUTED}>Noch keine Erzeugungsanlage angelegt.</p>}
          {assets.map((asset) => {
            const meter = groupOf(asset)?.meter ?? null;
            /* Hinter einem echten Zähler wird nichts weggenommen: Er misst die Anlage
               wirklich, und ein Wechsel auf ihr Gerät änderte still die Bilanz. Wer das
               will, löst sie dort unter „Ändern“. */
            const measured = meter !== null && !meter.virtual;
            return (
              <Checkbox
                key={asset.id}
                checked={assetIds.includes(asset.id)}
                disabled={measured}
                onChange={toggle(setAssetIds, asset.id)}
                label={[
                  asset.label,
                  kindLabel(asset.kind),
                  meter === null
                    ? "ohne Zähler"
                    : measured
                      ? `gemessen von ${meter.label}`
                      : `jetzt hinter ${meter.label}`,
                ].join(" · ")}
              />
            );
          })}
          <p style={MUTED}>
            Gezählt wird aus den Energieständen ihrer Geräte: der Ertrag einer PV-Anlage, beim
            Speicher die Entladung hinzu und die Ladung ab. Die Anlagen stehen danach hinter dem
            neuen Zähler.
          </p>
        </div>

        {meters.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {heading("Erzeugungszähler")}
            {meters.map((meter) => (
              <Checkbox
                key={meter.id}
                checked={meterIds.includes(meter.id)}
                onChange={toggle(setMeterIds, meter.id)}
                label={`${meter.label} · ${meterNumberText(meter)}`}
              />
            ))}
            <p style={MUTED}>
              Zwei Balkonkraftwerke mit je einer Messsteckdose: Ihre Zähler zählen zusammen. Was
              hinter ihnen steht, bleibt dort.
            </p>
          </div>
        )}
        {error !== null && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
}
